import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './Science.css';

function Science() {
  const particlesRef = useRef(null);

  // Vanta Waves setup
  // Particle Flow Animation setup
  useEffect(() => {
    const canvas = particlesRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    let animationId;

    // Utility functions
    const deg = (a) => Math.PI / 180 * a;
    const rand = (v1, v2) => Math.floor(v1 + Math.random() * (v2 - v1));

    // Configuration
    const opt = {
      particles: window.innerWidth / 500 ? 1000 : 500,
      noiseScale: 0.009, // Reduced from 0.009 for slower movement
      angle: Math.PI / 180 * -90,
      h1: 195, // #89f9ff - cyan
      h2: 200, // #0D394C - dark blue
      s1: 100, // Full saturation for bright cyan
      s2: 80,  // High saturation for dark blue
      l1: 85,  // High lightness for bright cyan
      l2: 25,  // Low lightness for dark blue
      strokeWeight: 1.2,
      tail: 82,
    };

    const Particles = [];
    let time = 0;
    let width, height;

    // Simple Perlin noise implementation
    function noise(x, y, z) {
      const X = Math.floor(x) & 255;
      const Y = Math.floor(y) & 255;
      const Z = Math.floor(z) & 255;
      x -= Math.floor(x);
      y -= Math.floor(y);
      z -= Math.floor(z);
      const u = fade(x);
      const v = fade(y);
      const w = fade(z);
      const A = p[X] + Y;
      const AA = p[A] + Z;
      const AB = p[A + 1] + Z;
      const B = p[X + 1] + Y;
      const BA = p[B] + Z;
      const BB = p[B + 1] + Z;
      return lerp(w, lerp(v, lerp(u, grad(p[AA], x, y, z), grad(p[BA], x - 1, y, z)), lerp(u, grad(p[AB], x, y - 1, z), grad(p[BB], x - 1, y - 1, z))), lerp(v, lerp(u, grad(p[AA + 1], x, y, z - 1), grad(p[BA + 1], x - 1, y, z - 1)), lerp(u, grad(p[AB + 1], x, y - 1, z - 1), grad(p[BB + 1], x - 1, y - 1, z - 1))));
    }

    function fade(t) { return t * t * t * (t * (t * 6 - 15) + 10); }
    function lerp(t, a, b) { return a + t * (b - a); }
    function grad(hash, x, y, z) {
      const h = hash & 15;
      const u = h < 8 ? x : y;
      const v = h < 4 ? y : h === 12 || h === 14 ? x : z;
      return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
    }

    // Permutation table
    const p = new Array(512);
    for (let i = 0; i < 256; i++) p[i] = p[i + 256] = Math.floor(Math.random() * 256);

    // Particle class
    class Particle {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.lx = x;
        this.ly = y;
        this.vx = 0;
        this.vy = 0;
        this.ax = 0;
        this.ay = 0;
        this.colorChoice = Math.random();
        this.setColor();
        this.maxSpeed = this.colorChoice > 0.66 ? 0.5 : this.colorChoice > 0.33 ? 1 : 0.7;
      }

      setColor() {
        if (this.colorChoice > 0.66) {
          //rgb(113, 241, 255) - bright cyan
          this.hue = 195;
          this.sat = 100;
          this.light = 85;
        } else if (this.colorChoice > 0.33) {
          //rgb(0, 255, 225) - dark blue
          this.hue = 200;
          this.sat = 80;
          this.light = 25;
        } else {
          // #00aed5 - medium blue
          this.hue = 195;
          this.sat = 100;
          this.light = 42;
        }
      }

      randomize() {
        this.colorChoice = Math.random();
        this.setColor();
        this.maxSpeed = this.colorChoice > 0.66 ? 1.5 : this.colorChoice > 0.33 ? 1.2 : 1;
      }

      update() {
        this.follow();

        this.vx += this.ax;
        this.vy += this.ay;

        const p = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        const a = Math.atan2(this.vy, this.vx);
        const m = Math.min(this.maxSpeed, p);
        this.vx = Math.cos(a) * m;
        this.vy = Math.sin(a) * m;

        this.x += this.vx;
        this.y += this.vy;
        this.ax = 0;
        this.ay = 0;

        this.edges();
      }

      follow() {
        const angle = (noise(this.x * opt.noiseScale, this.y * opt.noiseScale, time * opt.noiseScale)) * Math.PI * 0.5 + opt.angle;
        this.ax += Math.cos(angle);
        this.ay += Math.sin(angle);
      }

      updatePrev() {
        this.lx = this.x;
        this.ly = this.y;
      }

      edges() {
        if (this.x < 0) {
          this.x = width;
          this.updatePrev();
        }
        if (this.x > width) {
          this.x = 0;
          this.updatePrev();
        }
        if (this.y < 0) {
          this.y = height;
          this.updatePrev();
        }
        if (this.y > height) {
          this.y = 0;
          this.updatePrev();
        }
      }

      render() {
        context.strokeStyle = `hsla(${this.hue}, ${this.sat}%, ${this.light}%, 0.5)`;
        context.beginPath();
        context.moveTo(this.x, this.y);
        context.lineTo(this.lx, this.ly);
        context.stroke();
        this.updatePrev();
      }
    }

    function resize() {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    }

    function setup() {
      resize();
      Particles.length = 0;
      for (let i = 0; i < opt.particles; i++) {
        Particles.push(new Particle(Math.random() * width, Math.random() * height));
      }
      context.lineWidth = opt.strokeWeight;
    }

    function draw() {
      time++;
      context.fillStyle = `rgba(0, 7, 15, ${(100 - opt.tail) / 100})`; // #00070f background
      context.fillRect(0, 0, width, height);

      for (let p of Particles) {
        p.update();
        p.render();
      }
    }

    function step() {
      draw();
      animationId = requestAnimationFrame(step);
    }

    // Click handler to randomize colors
    function handleClick() {
      opt.angle += deg(rand(60, 60)) * (Math.random() > 0.5 ? 1 : -1);

      for (let p of Particles) {
        p.randomize();
      }
    }

    // Initialize
    setup();
    step();

    // Event listeners
    canvas.addEventListener('click', handleClick);
    window.addEventListener('resize', setup);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      canvas.removeEventListener('click', handleClick);
      window.removeEventListener('resize', setup);
    };
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-on-scroll');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-fade-in');
    animatedElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div id="science-page">
      <Helmet>
        <title>Science | NoetherTech</title>
      </Helmet>
      {/* Title Section - Immediate animation */}
      <div id="science-title">
        <canvas ref={particlesRef} className="particles-container"></canvas>
        <div id="titleTextContainer">
          <h1 className="animate-fade-in">THE SCIENCE BEHIND NOETHERTECH™</h1>
        </div>
      </div>

      {/* Heritage Section */}
      <section className="content-section hero-section">
        <div className="section-container">
          <div className="heritage-content">
            <div className="heritage-text">
              <h2 className="animate-fade-in">Our Inspiration</h2>
              <div className="section-divider animate-fade-in delay-1"></div>
              <p className="animate-fade-in delay-1">At the heart of NoetherTech™ lies the revolutionary work of Amalie Emmy Noether, arguably the most influential mathematician of the 20th century. Renowned mathematicians and physicists, including Albert Einstein, Jean Dieudonné, and Norbert Wiener, recognized her as the preeminent female mathematician in history. During her illustrious 27-year career, Noether's groundbreaking contributions transformed multiple mathematical domains, particularly in abstract algebra—a field that continues to shape modern mathematical thinking.</p>
              <p className="animate-fade-in delay-2">In 1915, Einstein himself sought Noether's expertise to resolve a critical challenge in his theory of general relativity. Her solution, now immortalized as Noether's theorem, established the fundamental connection between conservation laws and symmetries in nature. This theorem not only revolutionized theoretical physics but also fundamentally altered our understanding of physical phenomena. Today, NoetherTech™'s innovative technologies draw direct inspiration from this seminal work, embodying the elegant principles of symmetry and conservation that Noether uncovered.</p>
            </div>
            <div className="heritage-image animate-fade-in delay-2">
              <img 
                src="/emmy.png" 
                alt="Emmy Noether"
                className="noether-portrait"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Principles Section */}
      <section className="content-section highlight">
        <div className="section-container">
          <h2 className="animate-fade-in">Core Scientific Principles</h2>
          <div className="section-divider animate-fade-in delay-1"></div>
          <div className="principles-cards">
            <div className="principle-card animate-fade-in delay-2">
              <h3>Thermodynamic Systems</h3>
              <p>The brain operates as an open thermodynamic system, exchanging matter and energy with its environment while maintaining internal order.</p>
            </div>
            <div className="principle-card animate-fade-in delay-3">
              <h3>Neural Default States</h3>
              <p>The brain's default state operates at its lowest energy level, characterized by rhythmic electrical oscillations.</p>
            </div>
            <div className="principle-card animate-fade-in delay-4">
              <h3>Conservation & Symmetry</h3>
              <p>Greater EEG oscillation symmetry correlates with lower metabolic rates and enhanced cognitive performance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Text Content Section */}
      <section className="content-section">
        <div className="section-container">
          <h2 className="animate-fade-in science-explained-header">Our Science<br className="mobile-only" /> Explained</h2>
          <div className="section-divider animate-fade-in delay-1"></div>
          <div className="text-content">
            <p className="animate-fade-in delay-1">NoetherTech's EMBP™ technology is founded on fundamental physical principles governing neural systems: our approach models the brain as an open thermodynamic system with dissipative structure, operating under constraints of fixed free energy inflow from chemical nourishment and environmental information.</p>
            <p className="animate-fade-in delay-2">The brain's default state exhibits minimal energy consumption through rhythmic electrical oscillations, demonstrating adherence to conservation laws and symmetry principles. This oscillatory behavior's frequency selectivity correlates inversely with metabolic rate and positively with cognitive performance, suggesting that cognitive functions emerge as epiphenomena of the brain's energy optimization strategies.</p>
            <p className="animate-fade-in delay-3">In our thermodynamic model, neuronal organization is governed by minimal entropy principles. Massive synaptic connections reduce translational and rotational degrees of freedom, while coherent neuronal firing creates translational symmetry patterns. These mechanisms collectively minimize kinetic energy and optimize energy consumption, enhancing system stability.</p>
            <p className="animate-fade-in delay-4">Pathological conditions can disrupt these optimized states, leading to compensatory responses that prioritize survival over cognitive function. For instance, in cases of stroke or concussion, the brain adopts lower frequency activity patterns to conserve energy, regardless of impact on cognitive or sensorimotor functions.</p>
            <p className="animate-fade-in delay-4">The EMBP™ treatment protocol leverages these principles through a two-phase approach: electroencephalography (EEG) assessment of brain state deviations, followed by individualized transcranial magnetic stimulation (TMS) for state normalization. This methodology targets underlying energy state disruptions rather than treating phenomenological symptoms in isolation, offering a more fundamental approach to neurological intervention.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Science; 